import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CmSection, ContentType } from '../shared/model/cm/managed-content.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { UserSessionService } from '../shared/service/user-session.service';
import { PolicyService } from '../shared/service/policy.service';
import { GroupService } from '../shared/service/group.service';
import { AppInsightsApiService} from "../shared/service/app-insights-api.service";
import { MhsService } from '../shared/service/mhs.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { CUSTOM_ELEMENTS_SCHEMA, DoCheck } from '@angular/core';

import { RouterService } from '../shared/service/router.service';
import { ModalService } from '../shared/service/modal.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { EEOI_APP_ROUTES } from '../shared/global-constants';
import { ElectronicCommunicationComponent } from "../electronic-communication/electronic-communication.component";
import { OverviewComponent } from '../overview/overview.component';
import { AuthSignComponent } from '../auth-sign/auth-sign.component';
@Component({
  selector: 'app-review',
  standalone: true,
  imports: [OverviewComponent, AuthSignComponent, ElectronicCommunicationComponent],
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReviewComponent implements OnInit {

  // public readonly pageIllustrationUrl = 'assets/Illustration-Header-Base-Mountains.svg'

  @ViewChild(AuthSignComponent)
  authSignChild!: AuthSignComponent;
  @ViewChild(ElectronicCommunicationComponent)
  elecCommChild!: ElectronicCommunicationComponent;

  public isFormSubmitted!: boolean;

  showSuccessBanner = false;
  showAuthSignError = false;
  isSubmitting = false;
  private successToastTimer!: ReturnType<typeof setTimeout>;
  /**
   * Timestamp (ms) of the last real Submit-button click coming from
   * <app-electronic-communication>'s (submitAttempted) event. We use this as a
   * guard so that stray (val) emissions cannot navigate the user to
   * /confirmation without an explicit user click. The user reported the page
   * auto-submitting once both validations passed; that can happen if the
   * underlying web-component (radio/checkbox) re-fires a synthetic event.
   */
  private lastSubmitAttemptAt = 0;
  private static readonly SUBMIT_INTENT_WINDOW_MS = 60_000;

  /**
   * Bound predicate passed into <app-electronic-communication>'s
   * [externalValidationFn] so that the child aborts its submit pipeline if
   * auth-sign is not acknowledged. Bound here (rather than inline in the
   * template) so the function reference is stable and `this` is preserved.
   */
  public isAuthSignAcknowledgedBound = (): boolean => this.isAuthSignAcknowledged();
 
  




  constructor(private router: Router,
              private cmService: ManagedContentService,
              private activatedRoute: ActivatedRoute,
              private policyService: PolicyService,
              private userSessionService: UserSessionService,
              private groupService: GroupService,
              private appInsightsApiService: AppInsightsApiService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private mhsService: MhsService,
              private authGuardService: AuthGuardService,
              private pagetitleService: PagetitleService,
              private routerService: RouterService,
              private modalService: ModalService
              ) {}

  ngOnInit() {
    this.routerService.setRoute(this.router.url);
    this.appInsightsApiService.startNavigationEvent(this.router.url);
    this.userSessionService.data.sessionEnded = false;
    this.modalService.closeModal('prism-ui-modal-timer-modal');
    this.modalService.closeModal('prism-ui-modal-error-modal');
    
    this.isFormSubmitted = false;
    this.isSubmitting = false;
    if (this.shouldShowCoverageSavedToast()) {
      this.showSuccessBanner = true;
      this.scheduleSuccessToastDismiss();
    }

    this.appInsightsApiService.endNavigationEvent(this.router.url);
    this.pagetitleService.setPageTitle('Review');

  }

  onSuccessToastClose(): void {
    this.showSuccessBanner = false;
    if (this.successToastTimer) {
      clearTimeout(this.successToastTimer);
    }
  }

  private scheduleSuccessToastDismiss(): void {
    if (this.successToastTimer) {
      clearTimeout(this.successToastTimer);
    }
    this.successToastTimer = setTimeout(() => {
      this.showSuccessBanner = false;
    }, 2000);
  }

  private shouldShowCoverageSavedToast(): boolean {
    const showToastFromState = history.state?.showSuccess === true;
    const showToastFromSession = this.userSessionService.data['showCoverageSavedToast'] === true;
    const showToastFromStorage = sessionStorage.getItem('showCoverageSavedToast') === 'true';

    if (showToastFromState || showToastFromSession || showToastFromStorage) {
      this.userSessionService.data['showCoverageSavedToast'] = false;
      sessionStorage.removeItem('showCoverageSavedToast');
      return true;
    }

    return false;
  }

  canDeactivate(): boolean {
    return true;
  }

  public submitApplication(val: boolean): void {
    if (!val) {
      return;
    }

    // Guard: only proceed if the user actually clicked Submit recently.
    // Without this, any stray (val) emission from the elec-comm child (e.g. a
    // web-component re-firing a synthetic event) would silently navigate the
    // user away from the page.
    const elapsed = Date.now() - this.lastSubmitAttemptAt;
    if (this.lastSubmitAttemptAt === 0 || elapsed > ReviewComponent.SUBMIT_INTENT_WINDOW_MS) {
      return;
    }
    // Consume the intent so a single click cannot trigger more than one nav.
    this.lastSubmitAttemptAt = 0;

    // Validate that the Auth & Signature acknowledgement checkbox is checked
    // before allowing the application to be submitted. (Electronic-communication
    // validity is already guaranteed because the child only emits `val=true`
    // when its own form is valid.)
    if (!this.isAuthSignAcknowledged()) {
      this.markAuthSignAsTouched();
      this.scrollToAuthSignError();
      return;
    }

    this.isFormSubmitted = true;
    this.isSubmitting = true;
    this.router.navigate(['/confirmation']);
    const confirmationRoute = EEOI_APP_ROUTES.CONFIRMATION;
    this.appInsightsApiService.startNavigationEvent(confirmationRoute.toString());
  }

  /**
   * Invoked the moment the user clicks Submit in the electronic-communication
   * child, BEFORE that child runs its own form validation. We use this hook to
   * (a) force auth-sign validation at the same time so both sections show
   *     errors together, and
   * (b) record a "submit intent" timestamp that submitApplication() checks,
   *     so stray (val) emissions cannot bypass the user's click.
   */
  public onSubmitAttempt(): void {
    this.lastSubmitAttemptAt = Date.now();

    if (!this.isAuthSignAcknowledged()) {
      this.markAuthSignAsTouched();
      // Only scroll to auth-sign if elec-comm is valid (otherwise let the user
      // see the elec-comm error first since that's where they just clicked).
      if (this.isElecCommValid()) {
        this.scrollToAuthSignError();
      }
    }
  }

  /**
   * Returns true when the auth-sign acknowledgement checkbox is checked.
   */
  private isAuthSignAcknowledged(): boolean {
    const authSignForm = this.authSignChild?.authSignForm;
    if (!authSignForm) {
      // If the child hasn't rendered yet, treat as not acknowledged.
      return false;
    }
    return authSignForm.valid;
  }

  /**
   * Returns true when the electronic-communication form is valid (i.e. the
   * Yes/No radio is selected and any conditional email is filled in correctly).
   */
  private isElecCommValid(): boolean {
    const ecForm = this.elecCommChild?.electronicCommunicationForm;
    return !!ecForm && ecForm.valid;
  }

  /**
   * Marks the auth-sign acknowledgement control as touched/dirty so the
   * existing inline error message inside <prism-ui-form-field-select> renders.
   */
  private markAuthSignAsTouched(): void {
    const authSignForm = this.authSignChild?.authSignForm;
    if (!authSignForm) {
      return;
    }
    Object.keys(authSignForm.controls).forEach(key => {
      const control = authSignForm.get(key);
      control?.markAsTouched();
      control?.markAsDirty();
      control?.updateValueAndValidity();
    });
  }

  /**
   * Smoothly scrolls the auth-sign acknowledgement checkbox into view so the
   * user can see the validation error.
   */
  private scrollToAuthSignError(): void {
    setTimeout(() => {
      const target =
        document.querySelector('prism-ui-checkbox[key-id="agreedOrAcknowledged"]') ||
        document.querySelector('app-auth-sign');
      if (target) {
        (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  onCancel() {
    this.isFormSubmitted = false;
    this.modalService.showModal("prism-ui-modal-cancel-modal"); // Use the service to show the modal
  }

}
